import { File, ItemList } from 'v2/models';
import { IPath, FolderSelectedState, join } from './getFileTree';

/**
 * given an array of folder states, compute the state of of this folder
 */
export function summariseStates(
  manyFolderStates: (keyof typeof FolderSelectedState)[]
) {
  let hasSome = false;
  let hasEvery = false;
  let hasNone = false;
  manyFolderStates.forEach((state) => {
    switch (state) {
      case 'Some':
        hasSome = true;
        break;
      case 'Every':
        hasEvery = true;
        break;
      case 'None':
        hasNone = true;
        break;
    }
  });
  if (hasSome) {
    return 'Some';
  }
  if (hasNone && !hasEvery) {
    return 'None';
  }
  if (hasEvery && !hasNone) {
    return 'Every';
  }
  if (hasEvery && hasNone) {
    return 'Some';
  }

  return 'Every';
}

/**
 * given a list of files in, check if all, some or none of these files are
 * selected.
 * @param files All available files for selection
 * @param currentSel the list of currently selected files
 * @returns
 */
export function selectionFromFiles(
  files: File[],
  currentSel: ItemList
): FolderSelectedState {
  const fileIds = new Set(files.map(({ id }) => id));
  const selectionIds = new Set(currentSel.list.map(({ id }) => id));

  selectionIds.forEach((id) => fileIds.delete(id));

  if (fileIds.size === 0) {
    return FolderSelectedState.Every;
  }
  if (fileIds.size < files.length) {
    return FolderSelectedState.Some;
  }

  return FolderSelectedState.None;
}

/**
 * Recursive function that computes the selecion state for each of the folders in a file tree
 * @param node the foler node
 * @param currentSelection the currently selected files
 * @param results a map storing 'path/to/node' -> SelectionState for every node in the tree
 * @returns the computed selectio state of node
 */
export function computeFolderSelectionStateRec(
  node: IPath | undefined,
  currentSelection: ItemList,
  results?: Map<string, keyof typeof FolderSelectedState>
): keyof typeof FolderSelectedState {
  if (!node) {
    return 'Every';
  }
  const childStates = node.nodes.map((n) =>
    computeFolderSelectionStateRec(n, currentSelection, results)
  );
  const thisFolderFiles = selectionFromFiles(node.files, currentSelection);
  const thisState = summariseStates([...childStates, thisFolderFiles]);
  results?.set(join(node.dirname, node.basename), thisState);
  return thisState;
}
